import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6p58q130mftb86r3us0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation', label: 'Fence Installation' },
  { value: 'Fence Repair', label: 'Fence Repair' },
  { value: 'Gate Installation', label: 'Gate Installation' },
  { value: 'Fence Replacement', label: 'Fence Replacement' },
  { value: 'Other Fence Work', label: 'Other Fence Work' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function QuoteForm({ formId }: { formId: string }) {
  const [values, setValues] = useState<QuoteFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea id={`${formId}-project_details`} name="project_details" required rows={3} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe your fence project — material, approximate footage, property type..." />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
        {status === 'submitting' ? 'Sending...' : 'Request Free Fence Quote'}
      </button>
    </form>
  );
}

const faqs = [
  {
    q: 'Do I need a permit to build a fence in Boise?',
    shortA: 'It depends on height and location on your property.',
    fullA: 'In Boise, fences 6 feet or under in rear and side yards typically do not require a building permit. However, fences in the front yard, fences over 6 feet anywhere, and fences on corner lots often do require a permit. Rules also vary by zoning district. Always verify with the City of Boise Planning & Development Services before starting any fence project.',
  },
  {
    q: 'What is the maximum fence height allowed in Boise?',
    shortA: 'Generally 6 ft in rear/side yards, 4 ft in front yards — but it varies.',
    fullA: 'Boise\'s general residential zoning guidelines allow fences up to 6 feet in rear and side yards without a permit. Front yard fences are typically limited to 4 feet. Corner lots have additional visibility triangle requirements that may further restrict height near intersections. Some zones allow taller fences with a permit. Check your specific zoning district at the City of Boise\'s official code portal.',
  },
  {
    q: 'What is a "sight triangle" or visibility triangle?',
    shortA: 'A clear zone near intersections where fences must stay low.',
    fullA: 'A sight triangle (also called a visibility triangle or clear vision area) is a triangular zone at street intersections and driveways where fences, walls, and landscaping must be kept below a certain height — typically 3 feet — to ensure drivers and pedestrians can see each other clearly. If your property is on a corner lot or has a driveway near an intersection, your fence design must account for this. The exact dimensions vary by jurisdiction.',
  },
  {
    q: 'Do HOA rules override city permit rules?',
    shortA: 'Yes — HOA rules are separate and often stricter.',
    fullA: 'Your HOA\'s Covenants, Conditions & Restrictions (CC&Rs) are a private contract and operate independently of city zoning codes. Even if the city doesn\'t require a permit for your fence, your HOA may require written approval before you start. HOAs can restrict fence height, material, color, style, and setback from property lines. Always check with your HOA first — violations can result in fines or forced removal.',
  },
  {
    q: 'Do I need to call 811 before digging fence post holes?',
    shortA: 'Yes — it\'s required by Idaho law.',
    fullA: 'Idaho law requires all property owners and contractors to call 811 (or submit a request online at idaho811.com) at least two business days before any digging. Utility companies will mark underground lines — gas, electric, water, sewer, telecom — with colored flags or paint. Digging without a locate can result in serious injury, service outages, and significant fines. We coordinate this on every fence project we install.',
  },
  {
    q: 'Can I build a fence on the property line?',
    shortA: 'Usually yes, but verify your property lines first.',
    fullA: 'Most jurisdictions allow fences to be built on or near the property line, but you are responsible for knowing exactly where your property ends. Installing a fence even a few inches onto a neighbor\'s property can create legal disputes and require costly removal. If you\'re unsure of your property boundaries, a licensed surveyor can mark them before installation. Some homeowners choose to set their fence a few inches inside the property line to avoid any dispute.',
  },
  {
    q: 'Are there different rules for Ada County vs. City of Boise?',
    shortA: 'Yes — unincorporated Ada County has its own rules.',
    fullA: 'If your property is within the City of Boise limits, you follow Boise\'s municipal code. If you\'re in unincorporated Ada County (outside city limits), Ada County Development Services has its own zoning and permit requirements. Cities like Meridian, Eagle, Nampa, and Caldwell each have their own codes as well. Always confirm which jurisdiction governs your property before assuming any rule applies.',
  },
  {
    q: 'What happens if I build a fence without a required permit?',
    shortA: 'You may face fines, stop-work orders, or forced removal.',
    fullA: 'Building without a required permit can result in a stop-work order, fines, and a requirement to remove or modify the fence at your own expense. It can also create complications when selling your home, as unpermitted work may need to be disclosed or corrected before closing. If you\'re unsure whether a permit is needed, it\'s always safer to check first — the cost of a permit is far less than the cost of removal.',
  },
];

const checklist = [
  {
    icon: 'map-location-dot',
    title: 'Confirm your property lines',
    desc: 'Locate your survey stakes or hire a licensed surveyor. Never assume where your property ends.',
  },
  {
    icon: 'building-columns',
    title: 'Check your zoning district',
    desc: 'Look up your parcel on the City of Boise\'s GIS map or Ada County Assessor to confirm your zone and applicable rules.',
  },
  {
    icon: 'file-contract',
    title: 'Review your HOA CC&Rs',
    desc: 'Read your HOA\'s fence rules before designing anything. Submit for written approval if required — before breaking ground.',
  },
  {
    icon: 'clipboard-check',
    title: 'Determine if a permit is required',
    desc: 'Contact the City of Boise Planning & Development Services (or your local jurisdiction) to confirm permit requirements for your specific fence.',
  },
  {
    icon: 'phone',
    title: 'Call 811 before digging',
    desc: 'Submit a utility locate request at least 2 business days before any post holes are dug. Required by Idaho law.',
  },
  {
    icon: 'ruler-combined',
    title: 'Measure your sight triangles',
    desc: 'If you\'re on a corner lot or near a driveway, identify the visibility triangle zone and plan your fence to stay outside it.',
  },
  {
    icon: 'magnifying-glass',
    title: 'Check for easements',
    desc: 'Review your property deed or title report for utility easements. Fences built over easements may need to be removed if utility access is needed.',
  },
  {
    icon: 'handshake',
    title: 'Talk to your neighbors',
    desc: 'A quick conversation before installation can prevent disputes about placement, shared costs, or style preferences.',
  },
  {
    icon: 'file-lines',
    title: 'Get a written estimate with permit line item',
    desc: 'If a permit is required, make sure your contractor\'s estimate includes permit fees and who is responsible for pulling it.',
  },
];

const officialSources = [
  {
    name: 'City of Boise Planning & Development Services',
    desc: 'Permit requirements, zoning codes, and fence height rules for properties within Boise city limits.',
    url: 'https://www.cityofboise.org/departments/planning-and-development-services/',
    icon: 'building-columns',
  },
  {
    name: 'City of Boise Municipal Code (Online)',
    desc: 'Full text of Boise\'s zoning ordinance including fence and wall regulations by district.',
    url: 'https://www.cityofboise.org/departments/planning-and-development-services/planning/zoning-code/',
    icon: 'book-open',
  },
  {
    name: 'Ada County Development Services',
    desc: 'Rules for properties in unincorporated Ada County outside Boise city limits.',
    url: 'https://adacounty.id.gov/developmentservices/',
    icon: 'map',
  },
  {
    name: 'Idaho 811 — Call Before You Dig',
    desc: 'Submit a utility locate request online before any digging. Required by Idaho law.',
    url: 'https://www.idaho811.com/',
    icon: 'shovel',
  },
  {
    name: 'City of Meridian Building Department',
    desc: 'Permit and fence rules for properties within Meridian city limits.',
    url: 'https://meridiancity.org/building/',
    icon: 'city',
  },
  {
    name: 'City of Nampa Building Department',
    desc: 'Fence permit requirements and zoning rules for Nampa properties.',
    url: 'https://www.cityofnampa.us/175/Building',
    icon: 'city',
  },
];

export default function BoisePermitsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());

  const toggleFaq = (idx: number) => setOpenFaq(openFaq === idx ? null : idx);

  const toggleCheck = (idx: number) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useSEO({
    title: 'Boise Fence Rules & Permits: What Homeowners Should Know | Fix Right Services',
    description: 'Plain-language guide to Boise fence permit requirements, height limits, HOA rules, sight triangles, and Idaho 811 law. Includes a pre-build checklist and official source links.',
    keywords: 'Boise fence permit, fence rules Boise Idaho, fence height limit Boise, Ada County fence regulations, HOA fence rules Treasure Valley',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/resources/boise-fence-rules-permits`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'Boise & Ada County Fence Rules & Permits: What Homeowners Should Know',
      'description': 'Plain-language guide to fence permit requirements, height limits, HOA rules, and Idaho 811 law for Boise and Treasure Valley homeowners.',
      'author': {
        '@type': 'Organization',
        'name': 'Fix Right Services',
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Fix Right Services',
        'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
      },
      'dateModified': new Date().toISOString().split('T')[0],
    },
  });

  const completedCount = checkedItems.size;
  const totalCount = checklist.length;

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
                <Link to="/services/fence" className="hover:text-[#B45309] transition-colors cursor-pointer">Fence</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Page hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d9c5fdf3fefecc35033c8983308c71cb.jpg"
            alt="Boise Idaho residential neighborhood with fences"
            width="1920"
            height="650"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Services</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Fence Rules &amp; Permits</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-amber-600/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise &amp; Ada County, Idaho
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Boise &amp; Ada County Fence Rules &amp; Permits: What Homeowners Should Know
          </h1>
          <p className="text-lg text-white/90 mb-8 max-w-3xl">
            A plain-language guide to height limits, permit requirements, HOA rules, sight triangles, and Idaho 811 law — before you break ground on your next fence project.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="#checklist" className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="clipboard-check" className="w-4 h-4" /></span>
              Jump to Pre-Build Checklist
            </a>
            <a href="#official-sources" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/40 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-4 h-4" /></span>
              Official Source Links
            </a>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-amber-50 border-b border-amber-200" aria-label="Legal disclaimer">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-amber-200 rounded-lg flex-shrink-0 mt-0.5">
              <Icon name="triangle-exclamation" className="w-4 h-4 text-amber-700" />
            </span>
            <div>
              <p className="font-bold text-amber-900 text-sm mb-1">Not Legal Advice</p>
              <p className="text-amber-800 text-sm leading-relaxed">
                This page is for general informational purposes only and does not constitute legal, zoning, or permitting advice. Rules change, vary by zone, and may have been updated since this page was written. <strong>Always verify current requirements directly with your local jurisdiction</strong> — City of Boise, Ada County, or your specific city — before starting any fence project. Fix Right Services is not responsible for decisions made based on this content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Summary Cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Key rules summary">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">The Key Rules at a Glance</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Plain-language summaries of the most important concepts. Always verify with official sources before building.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ruler-vertical',
                title: 'Height Limits',
                color: 'bg-blue-50 border-blue-200',
                iconColor: 'text-blue-600',
                iconBg: 'bg-blue-100',
                points: [
                  'Rear & side yards: typically up to 6 ft without a permit',
                  'Front yards: typically limited to 4 ft',
                  'Over 6 ft anywhere usually requires a permit',
                  'Corner lots may have additional restrictions',
                ],
              },
              {
                icon: 'file-contract',
                title: 'When a Permit Is Required',
                color: 'bg-orange-50 border-orange-200',
                iconColor: 'text-[#B45309]',
                iconBg: 'bg-orange-100',
                points: [
                  'Fences over 6 ft in any yard',
                  'Any fence in the front yard (varies by zone)',
                  'Fences on corner lots near intersections',
                  'Retaining walls combined with fences',
                ],
              },
              {
                icon: 'eye',
                title: 'Sight Triangle / Visibility Zone',
                color: 'bg-yellow-50 border-yellow-200',
                iconColor: 'text-yellow-700',
                iconBg: 'bg-yellow-100',
                points: [
                  'Applies to corner lots and driveway exits',
                  'Fences must stay below ~3 ft in this zone',
                  'Ensures drivers can see pedestrians & traffic',
                  'Exact dimensions vary — verify with your city',
                ],
              },
              {
                icon: 'house-circle-check',
                title: 'HOA Rules',
                color: 'bg-green-50 border-green-200',
                iconColor: 'text-green-700',
                iconBg: 'bg-green-100',
                points: [
                  'Separate from city permits — both may apply',
                  'HOAs can restrict height, material, and color',
                  'Written approval often required before starting',
                  'Violations can result in fines or forced removal',
                ],
              },
              {
                icon: 'shovel',
                title: 'Idaho 811 — Call Before You Dig',
                color: 'bg-red-50 border-red-200',
                iconColor: 'text-red-600',
                iconBg: 'bg-red-100',
                points: [
                  'Required by Idaho law before any digging',
                  'Call or submit online at idaho811.com',
                  'Allow at least 2 business days for marking',
                  'Utilities will flag underground lines on your property',
                ],
              },
              {
                icon: 'map-location-dot',
                title: 'Property Lines & Easements',
                color: 'bg-purple-50 border-purple-200',
                iconColor: 'text-purple-600',
                iconBg: 'bg-purple-100',
                points: [
                  'You are responsible for knowing your boundaries',
                  'Survey stakes or a licensed surveyor confirm lines',
                  'Utility easements may restrict fence placement',
                  'Encroaching on a neighbor\'s land can be costly',
                ],
              },
            ].map((card) => (
              <div key={card.title} className={`rounded-2xl border p-6 ${card.color}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${card.iconBg}`}>
                    <Icon name={card.icon} className={`w-5 h-5 ${card.iconColor}`} />
                  </span>
                  <h3 className="font-bold text-gray-900 text-sm">{card.title}</h3>
                </div>
                <ul className="space-y-2">
                  {card.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-xs text-gray-700">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="circle-dot" className={`w-3 h-3 ${card.iconColor}`} />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Height & Front Yard Detail */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Height and front yard rules detail">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Height Rules */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg">
                  <Icon name="ruler-vertical" className="w-5 h-5 text-[#B45309]" />
                </span>
                Fence Height: What the Rules Generally Say
              </h2>
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                <p>
                  In most Boise residential zones, the general rule of thumb is:
                </p>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-900 text-white">
                        <th className="text-left px-4 py-3 font-semibold">Location on Property</th>
                        <th className="text-left px-4 py-3 font-semibold">Typical Max Height</th>
                        <th className="text-left px-4 py-3 font-semibold">Permit Usually?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { loc: 'Rear yard', height: 'Up to 6 ft', permit: 'No (under 6 ft)' },
                        { loc: 'Side yard', height: 'Up to 6 ft', permit: 'No (under 6 ft)' },
                        { loc: 'Front yard', height: 'Up to 4 ft', permit: 'Often yes' },
                        { loc: 'Corner lot (near intersection)', height: 'Varies — sight triangle applies', permit: 'Often yes' },
                        { loc: 'Any location, over 6 ft', height: 'Allowed with permit', permit: 'Yes' },
                      ].map((row, i) => (
                        <tr key={row.loc} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 font-medium text-gray-900">{row.loc}</td>
                          <td className="px-4 py-3 text-gray-700">{row.height}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${row.permit.startsWith('No') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {row.permit}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 text-xs">
                    <strong>Important:</strong> These are general guidelines for common residential zones. Your specific parcel may be in a zone with different rules. Always verify with the City of Boise or your local jurisdiction before building.
                  </p>
                </div>
              </div>
            </div>

            {/* Sight Triangle */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg">
                  <Icon name="eye" className="w-5 h-5 text-[#B45309]" />
                </span>
                Front Yard Visibility &amp; Sight Triangles
              </h2>
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Front yard fences</strong> are more regulated than rear or side yard fences because they affect street visibility and neighborhood character. In Boise, front yard fences are generally limited to <strong>4 feet in height</strong>, though this varies by zone.
                </p>
                <p>
                  <strong>Sight triangles</strong> are a separate concept that applies specifically to corner lots and properties with driveways near intersections. The idea is simple: within a triangular zone measured from the corner of two streets (or a driveway and a street), fences, walls, and landscaping must be kept below a certain height — typically <strong>3 feet</strong> — so that drivers can see pedestrians and oncoming traffic.
                </p>
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                  <p className="font-semibold text-gray-900 text-sm">If you have a corner lot or driveway near an intersection:</p>
                  <ul className="space-y-2">
                    {[
                      'Identify the sight triangle zone before designing your fence',
                      'Keep fence height at or below 3 ft within that zone',
                      'Confirm exact triangle dimensions with your city',
                      'Landscaping (shrubs, trees) also counts — not just fences',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-gray-700">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="check" className="w-3 h-3 text-[#B45309]" />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-gray-500 italic">
                  The exact dimensions of sight triangles vary by jurisdiction and road type. Verify with the City of Boise Planning &amp; Development Services or your local city before finalizing your fence design.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Official Sources */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" id="official-sources" aria-label="Official source links">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Where to Verify: Official Sources</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These are the official government and utility resources where you can confirm current rules for your specific property. Always go to the source.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {officialSources.map((source) => (
              <a
                key={source.name}
                href={source.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group bg-gray-50 hover:bg-[#B45309]/5 border border-gray-200 hover:border-[#B45309]/30 rounded-xl p-5 transition-all cursor-pointer"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0">
                    <Icon name={source.icon} className="w-4 h-4 text-[#B45309]" />
                  </span>
                  <h3 className="font-semibold text-gray-900 text-sm leading-snug group-hover:text-[#B45309] transition-colors">{source.name}</h3>
                </div>
                <p className="text-gray-600 text-xs leading-relaxed mb-3">{source.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#B45309] text-xs font-semibold">
                  Visit official site
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-3 h-3" /></span>
                </span>
              </a>
            ))}
          </div>
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
            <p className="text-gray-600 text-sm">
              <strong>Not sure which jurisdiction covers your property?</strong> Check the Ada County Assessor&apos;s parcel viewer at{' '}
              <a href="https://adacounty.id.gov/assessor/" target="_blank" rel="nofollow noopener noreferrer" className="text-[#B45309] hover:underline font-medium cursor-pointer">adacounty.id.gov/assessor</a>
              {' '}to look up your parcel and confirm your city or county jurisdiction.
            </p>
          </div>
        </div>
      </section>

      {/* Pre-Build Checklist */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" id="checklist" aria-label="Pre-build checklist">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pre-Build Checklist for Boise Homeowners</h2>
            <p className="text-gray-600">
              Work through these steps before your fence installation begins. Check each item off as you go.
            </p>
            {completedCount > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 text-sm font-medium px-4 py-2 rounded-full">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="check-circle" className="w-4 h-4" /></span>
                {completedCount} of {totalCount} completed
              </div>
            )}
          </div>
          <div className="space-y-3">
            {checklist.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => toggleCheck(idx)}
                className={`w-full flex items-start gap-4 p-5 rounded-xl border text-left transition-all cursor-pointer ${
                  checkedItems.has(idx)
                    ? 'bg-green-50 border-green-300'
                    : 'bg-white border-gray-200 hover:border-[#B45309]/30 hover:bg-[#B45309]/5'
                }`}
                aria-pressed={checkedItems.has(idx)}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 transition-colors ${
                  checkedItems.has(idx)
                    ? 'bg-green-500 border-green-500'
                    : 'border-gray-300'
                }`}>
                  {checkedItems.has(idx) && <Icon name="check" className="w-3 h-3 text-white" />}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon} className={`w-4 h-4 ${checkedItems.has(idx) ? 'text-green-600' : 'text-[#B45309]'}`} />
                    </span>
                    <p className={`font-semibold text-sm ${checkedItems.has(idx) ? 'text-green-800 line-through' : 'text-gray-900'}`}>
                      {item.title}
                    </p>
                  </div>
                  <p className={`text-xs leading-relaxed ml-7 ${checkedItems.has(idx) ? 'text-green-700' : 'text-gray-600'}`}>
                    {item.desc}
                  </p>
                </div>
              </button>
            ))}
          </div>
          {completedCount === totalCount && (
            <div className="mt-6 bg-green-50 border border-green-300 rounded-xl p-5 text-center">
              <span className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                <Icon name="circle-check" className="w-6 h-6 text-green-600" />
              </span>
              <p className="font-bold text-green-800 mb-1">Checklist complete!</p>
              <p className="text-green-700 text-sm">You&apos;re ready to move forward. Ready to get a quote?</p>
              <a href="#fence-quote" className="inline-flex items-center gap-2 mt-3 bg-[#B45309] hover:bg-[#92400E] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Request Free Estimate
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Related resources">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Fence Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: 'fence',
                title: 'Fence Installation & Repair Service',
                desc: 'See what we install, materials, and how we work.',
                to: '/services/fence',
                cta: 'View service page',
              },
              {
                icon: 'dollar-sign',
                title: 'Fence Cost Guide — Boise',
                desc: 'Real price ranges by material with "depends on" notes.',
                to: '/blog/fence-installation-cost-guide-boise-idaho',
                cta: 'Read cost guide',
              },
              {
                icon: 'clock',
                title: 'Fence Installation Timeline',
                desc: 'What to expect from first call to finished fence.',
                to: '/blog/fence-installation-timeline-boise',
                cta: 'Read timeline guide',
              },
              {
                icon: 'phone',
                title: 'Get a Free Quote',
                desc: 'Tell us about your project — we respond within 24 hours.',
                to: '#fence-quote',
                cta: 'Request estimate',
                isAnchor: true,
              },
            ].map((item) =>
              item.isAnchor ? (
                <a
                  key={item.title}
                  href={item.to}
                  className="group bg-[#B45309]/5 hover:bg-[#B45309]/10 border border-[#B45309]/20 hover:border-[#B45309]/40 rounded-xl p-5 transition-all cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg mb-3">
                    <Icon name={item.icon} className="w-4 h-4 text-[#B45309]" />
                  </span>
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{item.title}</h3>
                  <p className="text-gray-600 text-xs mb-3">{item.desc}</p>
                  <span className="text-[#B45309] text-xs font-semibold flex items-center gap-1">
                    {item.cta}
                    <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                  </span>
                </a>
              ) : (
                <Link
                  key={item.title}
                  to={item.to}
                  className="group bg-gray-50 hover:bg-[#B45309]/5 border border-gray-200 hover:border-[#B45309]/30 rounded-xl p-5 transition-all cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg mb-3">
                    <Icon name={item.icon} className="w-4 h-4 text-[#B45309]" />
                  </span>
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{item.title}</h3>
                  <p className="text-gray-600 text-xs mb-3">{item.desc}</p>
                  <span className="text-[#B45309] text-xs font-semibold flex items-center gap-1">
                    {item.cta}
                    <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                  </span>
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Frequently asked questions">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Fence Permit FAQs</h2>
            <p className="text-gray-600">Common questions from Boise homeowners about fence rules, permits, and regulations.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <div className="pr-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      <a id={`faq-permits-${idx}`}>{faq.q}</a>
                    </h4>
                    <p className="text-[#B45309] text-sm font-medium">{faq.shortA}</p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.fullA}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'mainEntity': faqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.shortA + ' ' + faq.fullA,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Call to action">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Ready to get your fence installed the right way?</p>
            <p className="text-white/85 text-sm">We handle permits, 811 locates, and HOA guidance. Free estimates, response within 24 hours.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a href="tel:+12082046331" className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a href="#fence-quote" className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
              Request Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="fence-quote" aria-label="Request a fence quote">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Get Your Free Fence Estimate</h2>
            <p className="text-gray-600">
              Tell us about your project and we&apos;ll get back to you within 24 hours with a free, no-obligation estimate.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4 text-[#B45309]" /></span>
              Prefer to call?&nbsp;
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>
            </div>
          </div>
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
            <QuoteForm formId="permits-page-quote-form" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted fence installation and repair experts serving the entire Treasure Valley.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Fence Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/locations/fence-installation-boise-idaho" className="hover:text-white transition-colors cursor-pointer">Fence Installation in Boise</Link></li>
                <li><Link to="/blog/fence-installation-cost-guide-boise-idaho" className="hover:text-white transition-colors cursor-pointer">Fence Cost Guide</Link></li>
                <li><Link to="/resources/boise-fence-rules-permits" className="text-[#F59E0B] hover:text-white transition-colors cursor-pointer">Fence Rules &amp; Permits</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Services</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Boise, Meridian, Nampa &amp; Treasure Valley</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
